import { createBrowserRouter, Outlet } from 'react-router-dom';

import ClientContainer from '@/services/client/ClientContainer';
import PaymentsConfigContainer from '@/services/paymentsConfig/PaymentsConfigContainer';
import { ErrorContainer } from '@/services/error/ErrorContainer';
import SeoContainer from '@/services/seo/SeoContainer';
import UrlOverridesContainer from '@/services/urlOverrides/UrlOverridesContainer';
import ProvidersLibsContainer from '@/services/providersLibs/ProvidersLibsContainer';
import DonateLayout from '@/components/layout/DonateLayout';
import SimpleLayout from '@/components/layout/SimpleLayout';
import GeneralError from '@/features/generalError/GeneralError';
import NotFound from '@/features/notFound/NotFound';
import JourneySelect from '@/features/journeySelect/JourneySelect';
import DirectPaymentClientContainer from '@/features/directPayment/DirectPaymentClientContainer';
import DirectJourneyContainer from '@/features/directPayment/DirectJourneyContainer';
import CartContainer from '@/services/cart/CartContainer';
import GtmContainer from '@/services/gtm/GtmContainer';
import ScrollToTop from '@/components/ScrollToTop';
import StaleJourneyContainer from '@/services/journeys/StaleJourneyContainer';

import donateSingleRoutes from './donateSingleRoutes';
import donateMonthlyRoutes from './donateMonthlyRoutes';
import payinRoutes from './payinRoutes';
import directPaymentRoutes from './directPaymentRoutes';
import adoptARedNoseRoutes from './adoptARedNoseRoutes';

const isDirectPaymentMode = import.meta.env.VITE_DIRECT_PAYMENT_MODE === 'true';

export default createBrowserRouter(
  [
    // A general error route, outside of the root to avoid running logic in the containers
    // This structure is used to avoid having container errors triggering a redirect loop from the error route itself
    {
      path: '/error',
      element: (
        <SimpleLayout>
          <ScrollToTop />
          <GeneralError />
        </SimpleLayout>
      ),
    },
    // Direct payment journey route (used for prizes, etc); configured separately to donation journeys
    // as it uses a simple layout (i.e. without using cart data), only available when appropriate:
    ...(isDirectPaymentMode ? [
      {
        path: '/pay',
        element: (
          <DirectPaymentClientContainer>
            <PaymentsConfigContainer>
              <CartContainer>
                <ErrorContainer>
                  <SeoContainer>
                    <UrlOverridesContainer>
                      <GtmContainer>
                        <ProvidersLibsContainer>
                          <DirectJourneyContainer>
                            <SimpleLayout>
                              <ScrollToTop />
                              <Outlet />
                            </SimpleLayout>
                          </DirectJourneyContainer>
                        </ProvidersLibsContainer>
                      </GtmContainer>
                    </UrlOverridesContainer>
                  </SeoContainer>
                </ErrorContainer>
              </CartContainer>
            </PaymentsConfigContainer>
          </DirectPaymentClientContainer>
        ),
        children: [
          directPaymentRoutes, // path (set above) = /pay/:transactionId
        ],
      },
      // Error screen if a user is trying to access the root/base
      {
        path: '/',
        element: (
          <SimpleLayout>
            <ScrollToTop />
            <GeneralError errorType="root-not-allowed" />
          </SimpleLayout>
        ),
      },
    ] : []),
    // The base of the application, wrapping the donation journeys (contains *most* of
    // the routes in the application), also only available when appropriate:
    ...(!isDirectPaymentMode ? [{
      path: '/',
      element: (
        <ClientContainer>
          <PaymentsConfigContainer>
            <CartContainer>
              <ErrorContainer>
                <SeoContainer>
                  <UrlOverridesContainer>
                    <GtmContainer>
                      <DonateLayout>
                        <ScrollToTop />
                        <StaleJourneyContainer>
                          <Outlet />
                        </StaleJourneyContainer>
                      </DonateLayout>
                    </GtmContainer>
                  </UrlOverridesContainer>
                </SeoContainer>
              </ErrorContainer>
            </CartContainer>
          </PaymentsConfigContainer>
        </ClientContainer>
      ),
      children: [
        // Handle any URL that is *not* matched by a route below...
        {
          path: '*',
          element: <NotFound />,
        },
        // Index page to choose journey
        {
          path: '/',
          element: <JourneySelect />,
        },
        donateSingleRoutes, // path = /donate/*
        donateMonthlyRoutes, // path = /donate-monthly/*
        payinRoutes, // path = /payin/*
        adoptARedNoseRoutes, // path = /adopt-a-red-nose/*
      ],
    }] : []),
  ],
  // For future reference; we might need use and dynamically set this
  // to allow subdirectories within our our one single Payments PR env?
  // { basename: '/my-branch-name' }
);
